import { T, formatCents, formatCount, formatDateTime, tt } from '@agos/shared'
import { ChevronDown, Loader2, MapPin } from 'lucide-react'

import {
  isUnknownStatus,
  orderTypeLabel,
  statusLabel,
  statusTone,
  type CustomerOrder,
  type OrderLineRow,
  type OrderTotals,
} from './orders'

/**
 * One past order, as legacy draws it — §9.247, FB-407, the owner's N2.
 *
 * *"história objednávka alias Moje objednávky, is missing what was actually ordered… that UI looks
 * shitty."* It was a line of text — number, status, total — with three buttons stacked under it. No
 * date, no address, no type, and no items at all, so one order could not be told from another.
 *
 * **A separate file on purpose.** The last attempt to restructure `ProfilePage.tsx` in place spliced
 * across a JSX boundary and rendered the addresses section once per game; a card that owns its own
 * markup cannot do that to its neighbours.
 *
 * The items arrive on the click that opens the card, exactly as `again()` already fetches the same
 * order to rebuild a basket — §9.21 made this section lazy to keep the catalogue off the page, and an
 * eager per-order fetch would undo that twenty times over.
 */

const TONE_CLASSES: Record<ReturnType<typeof statusTone>, string> = {
  done: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  stopped: 'bg-red-500/15 text-red-600 dark:text-red-400',
  active: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
}

export type OrderDetail =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'loaded'; items: OrderLineRow[]; totals: OrderTotals }

export function OrderHistoryCard({
  order,
  detail,
  onToggle,
  children,
}: {
  order: CustomerOrder
  /** Absent means the card is shut and nothing has been asked for. */
  detail: OrderDetail | null
  onToggle: () => void
  /** The three controls — cancel, track, reorder — which stay exactly as they were. */
  children: React.ReactNode
}) {
  const open = detail !== null

  return (
    <div className="rounded-2xl border border-border overflow-hidden" data-order={order.id}>
      <button
        type="button"
        data-order-open={order.id}
        aria-expanded={open}
        onClick={onToggle}
        className="w-full text-left p-4 space-y-2 hover:bg-muted/30 active:bg-muted/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">
              <T>Objednávka</T> #{order.orderNumber}
            </p>
            {/* The date, which the row had never carried - a customer could not tell last Tuesday's
                order from last month's. */}
            {order.createdAt !== '' && (
              <p className="text-xs text-muted-foreground" data-order-date>
                {formatDateTime(order.createdAt)}
              </p>
            )}
          </div>
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full shrink-0 ${TONE_CLASSES[statusTone(order.status)]}`}
            data-order-status={order.status}
          >
            {statusLabel(order.status)}
            {/* A status the port has never seen prints its own key rather than a guess, and says so
                - the list can grow on the server without this screen inventing a label. */}
            {isUnknownStatus(order.status) && <span data-order-status-unknown />}
          </span>
        </div>

        {order.customerAddress !== '' && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-order-address>
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{order.customerAddress}</span>
          </span>
        )}

        <div className="flex items-center justify-between pt-1 gap-2">
          <span className="text-sm text-muted-foreground" data-order-type={order.type}>
            {orderTypeLabel(order.type)}
          </span>
          <span className="flex items-center gap-2">
            <span className="font-semibold text-foreground" data-order-total>
              {formatCents(order.totalCents)}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </span>
        </div>
      </button>

      {detail !== null && (
        <div className="border-t border-border bg-muted/20 p-4 space-y-2" data-order-detail={order.id}>
          {detail.kind === 'loading' && (
            <div className="flex justify-center py-2" data-order-items-loading>
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          )}
          {detail.kind === 'error' && (
            <p className="text-sm text-red-500" data-order-items-error>
              <T>Nepodarilo sa načítať položky.</T>
            </p>
          )}
          {detail.kind === 'loaded' && detail.items.length === 0 && (
            <p className="text-sm text-muted-foreground" data-order-items-empty>
              <T>Žiadne položky</T>
            </p>
          )}
          {detail.kind === 'loaded' && detail.items.length > 0 && (
            <>
              <ul className="space-y-1.5" data-order-items={detail.items.length}>
                {detail.items.map((item) => (
                  <li key={item.id} className="flex items-start justify-between gap-3 text-sm" data-order-item>
                    <span className="min-w-0 flex-1">
                      <span className="text-foreground break-words">
                        <span className="font-semibold text-muted-foreground tabular-nums">
                          {formatCount(item.quantity)}×
                        </span>{' '}
                        {item.name}
                        {/* Marked as a gift, as the basket marks one - `giftRuleId` is on the line
                            and no screen had read it here. */}
                        {item.isGift && (
                          <span
                            className="ml-2 text-[10px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-500/15 px-1.5 py-0.5 rounded"
                            data-order-item-gift
                          >
                            <T>Darček</T>
                          </span>
                        )}
                      </span>
                      {item.boxGroupName !== null && (
                        <span className="block text-xs text-muted-foreground mt-0.5" data-order-item-box>
                          {item.boxGroupName}
                          {item.boxSlotLabel !== null && ` · ${item.boxSlotLabel}`}
                        </span>
                      )}
                    </span>
                    <span className="text-foreground font-medium tabular-nums whitespace-nowrap">
                      {formatCents(item.totalCents)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-border pt-2 mt-2 space-y-1 text-xs" data-order-totals>
                <p className="flex justify-between text-muted-foreground">
                  <span>
                    <T>Medzisúčet</T>
                  </span>
                  <span className="tabular-nums">{formatCents(detail.totals.subtotalCents)}</span>
                </p>
                {/* Only when there is one: a *Rozvoz 0,00 €* line on a pickup order is noise, and a
                    *Zľava 0,00 €* reads as a discount that failed. */}
                {detail.totals.deliveryCents !== null && (
                  <p className="flex justify-between text-muted-foreground" data-order-delivery>
                    <span>
                      <T>Rozvoz</T>
                    </span>
                    <span className="tabular-nums">{formatCents(detail.totals.deliveryCents)}</span>
                  </p>
                )}
                {detail.totals.discountCents !== null && (
                  <p className="flex justify-between text-muted-foreground" data-order-discount>
                    <span>
                      <T>Zľava</T>
                    </span>
                    <span className="tabular-nums">−{formatCents(detail.totals.discountCents)}</span>
                  </p>
                )}
                {/* Legacy closes the block with the order's own total, `MojProfil.tsx:2087` - the
                    same figure as the collapsed row, so the parts above are seen to add up to it. */}
                <p
                  className="flex justify-between font-semibold text-sm text-foreground pt-1 border-t border-border"
                  data-order-grand-total
                >
                  <span>
                    <T>Spolu</T>
                  </span>
                  <span className="tabular-nums">{formatCents(order.totalCents)}</span>
                </p>
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2 pt-1" aria-label={tt('Akcie objednávky')}>
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
